/**
 * @file BenefitList.js
 * @description List view of benefit enrollments. Renders a table on desktop and a collapsible accordion on mobile, using a dash for any missing enrollment data.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';

import styles from './BenefitList.module.scss';
import NotFoundBox from '../../ui/no_data_indicators/NotFoundBox';
import useIsMobile from '../_hooks/useIsMobile';
import { formatCoverageDateMMDDYYYY } from '../_helpers/dateHelpers';
import formatCurrencyString, {
    formatCurrencyStringV2,
} from '../../../utils/formatCurrency';
import { ROUTE_BUILDERS } from '../../../constants/routes';
import { getText } from '../../../i18n';

const LOCALE = 'en-US';
const CURRENCY = 'USD';
const EMPTY_VALUE = '-';

function formatDateRange(startDate, endDate) {
    const startLabel = formatCoverageDateMMDDYYYY(startDate);
    if (!startLabel) {
        return EMPTY_VALUE;
    }
    const endLabel = formatCoverageDateMMDDYYYY(endDate);
    return endLabel ? `${startLabel} - ${endLabel}` : startLabel;
}

function formatTier(enrollment) {
    if (!enrollment?.tier) {
        return EMPTY_VALUE;
    }
    return enrollment.tier;
}

function formatVolume(enrollment) {
    if (
        enrollment?.volume === null ||
        enrollment?.volume === undefined ||
        enrollment?.volume === ''
    ) {
        return EMPTY_VALUE;
    }

    const formatted = formatCurrencyStringV2(enrollment.volume, {
        locale: LOCALE,
        currency: CURRENCY,
        emptyDisplay: EMPTY_VALUE,
    });

    return formatted === EMPTY_VALUE ? String(enrollment.volume) : formatted;
}

function formatMemo(enrollment) {
    if (!enrollment?.memo) {
        return EMPTY_VALUE;
    }

    return String(enrollment.memo).trim();
}

function formatCost(value, isEnabled) {
    if (!isEnabled) {
        return null;
    }
    return formatCurrencyString(value, {
        locale: LOCALE,
        currency: CURRENCY,
        emptyDisplay: EMPTY_VALUE,
    });
}

function buildPlanLabel(enrollment) {
    return enrollment.planType
        ? `${enrollment.planType} - ${enrollment.plan}`
        : enrollment.plan || getText('msg_82a78c8f567d');
}

function DetailsLink({ enrollment, planYearId }) {
    if (!enrollment.planId) {
        return <span className={styles.detailsPlaceholder}>{EMPTY_VALUE}</span>;
    }
    return (
        <Link
            to={ROUTE_BUILDERS.myBenefitsPlanDetails(
                enrollment.planId,
                planYearId,
            )}
            className={styles.detailsLink}
            aria-label={`View details for ${buildPlanLabel(enrollment)}`}
        >
            <i className="bi bi-arrow-up-right-circle-fill" />
        </Link>
    );
}

function DesktopRow({
    enrollment,
    planYearId,
    displayEmployeeCosts,
    displayEmployerCosts,
    columnCount,
}) {
    const employeeCost = formatCost(
        enrollment.employeeCost,
        displayEmployeeCosts,
    );
    const employerCost = formatCost(
        enrollment.employerCost,
        displayEmployerCosts,
    );
    return (
        <div className={styles.row} data-cols={columnCount}>
            <div className={styles.planName}>{buildPlanLabel(enrollment)}</div>
            <div className={styles.cellText}>
                {formatDateRange(enrollment.startDate, enrollment.endDate)}
            </div>
            <div className={styles.cellText}>{formatTier(enrollment)}</div>
            <div className={styles.cellText}>{formatVolume(enrollment)}</div>
            <div className={styles.cellText}>{formatMemo(enrollment)}</div>
            {displayEmployeeCosts && (
                <div className={`${styles.costCell} ${styles.cellText}`}>
                    {employeeCost}
                </div>
            )}
            {displayEmployerCosts && (
                <div className={`${styles.costCell} ${styles.cellText}`}>
                    {employerCost}
                </div>
            )}
            <div className={styles.detailsCell}>
                <DetailsLink enrollment={enrollment} planYearId={planYearId} />
            </div>
        </div>
    );
}

function AccordionRow({
    enrollment,
    planYearId,
    displayEmployeeCosts,
    displayEmployerCosts,
}) {
    const [isExpanded, setIsExpanded] = useState(false);
    const isEnrolled = Boolean(enrollment.startDate);
    const planLabel = buildPlanLabel(enrollment);

    if (!isEnrolled) {
        if (!enrollment.planId) {
            return (
                <div className={styles.accordionRow}>
                    <div className={styles.accordionRowPlain}>
                        <span className={styles.accordionHeaderLabel}>
                            {planLabel}
                        </span>
                    </div>
                </div>
            );
        }
        return (
            <Link
                to={ROUTE_BUILDERS.myBenefitsPlanDetails(
                    enrollment.planId,
                    planYearId,
                )}
                className={`${styles.accordionRow} ${styles.accordionRowLink}`}
                aria-label={`View details for ${planLabel}`}
            >
                <span className={styles.accordionHeaderLabel}>{planLabel}</span>
                <i className="bi bi-chevron-right" />
            </Link>
        );
    }

    const employeeCost = formatCost(
        enrollment.employeeCost,
        displayEmployeeCosts,
    );
    const employerCost = formatCost(
        enrollment.employerCost,
        displayEmployerCosts,
    );

    return (
        <div className={styles.accordionRow}>
            <button
                type="button"
                className={styles.accordionHeader}
                onClick={() => setIsExpanded(!isExpanded)}
                aria-expanded={isExpanded}
            >
                <span className={styles.accordionHeaderLabel}>{planLabel}</span>
                <i
                    className={`bi ${
                        isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'
                    }`}
                />
            </button>
            {isExpanded && (
                <div className={styles.accordionBody}>
                    <div className={styles.accordionItem}>
                        <span className={styles.accordionItemLabel}>
                            {getText('msg_6bb4b674b323')}
                        </span>
                        <span className={styles.accordionItemValue}>
                            {formatDateRange(
                                enrollment.startDate,
                                enrollment.endDate,
                            )}
                        </span>
                    </div>
                    <div className={styles.accordionItem}>
                        <span className={styles.accordionItemLabel}>
                            {getText('msg_5bd44ebe63cd')}
                        </span>
                        <span className={styles.accordionItemValue}>
                            {formatTier(enrollment)}
                        </span>
                    </div>
                    <div className={styles.accordionItem}>
                        <span className={styles.accordionItemLabel}>
                            {getText('msg_80e13549b47a')}
                        </span>
                        <span className={styles.accordionItemValue}>
                            {formatVolume(enrollment)}
                        </span>
                    </div>
                    <div className={styles.accordionItem}>
                        <span className={styles.accordionItemLabel}>
                            {getText('msg_3f18c98c7e88')}
                        </span>
                        <span className={styles.accordionItemValue}>
                            {formatMemo(enrollment)}
                        </span>
                    </div>
                    {displayEmployeeCosts && (
                        <div className={styles.accordionItem}>
                            <span className={styles.accordionItemLabel}>
                                {getText('msg_143bd56dad5f')}
                            </span>
                            <span className={styles.accordionItemValue}>
                                {employeeCost}
                            </span>
                        </div>
                    )}
                    {displayEmployerCosts && (
                        <div className={styles.accordionItem}>
                            <span className={styles.accordionItemLabel}>
                                {getText('msg_330085f1641c')}
                            </span>
                            <span className={styles.accordionItemValue}>
                                {employerCost}
                            </span>
                        </div>
                    )}
                    {enrollment.planId && (
                        <Link
                            to={ROUTE_BUILDERS.myBenefitsPlanDetails(
                                enrollment.planId,
                                planYearId,
                            )}
                            className={styles.accordionDetailsLink}
                        >
                            {getText('msg_2a8caedb272b')}
                            <span className={styles.accordionDetailsIcon}>
                                <i className="bi bi-arrow-up-right" />
                            </span>
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
}

function DesktopTable({
    enrollments,
    planYearId,
    displayEmployeeCosts,
    displayEmployerCosts,
}) {
    const columnCount =
        5 + (displayEmployeeCosts ? 1 : 0) + (displayEmployerCosts ? 1 : 0) + 1;
    return (
        <div className={styles.table}>
            <div className={styles.headRow} data-cols={columnCount}>
                <div>{getText('msg_ae2f98a099fe')}</div>
                <div>{getText('msg_6bb4b674b323')}</div>
                <div>{getText('msg_5bd44ebe63cd')}</div>
                <div>{getText('msg_80e13549b47a')}</div>
                <div>{getText('msg_3f18c98c7e88')}</div>
                {displayEmployeeCosts && (
                    <div className={styles.costCell}>
                        {getText('msg_143bd56dad5f')}
                    </div>
                )}
                {displayEmployerCosts && (
                    <div className={styles.costCell}>
                        {getText('msg_330085f1641c')}
                    </div>
                )}
                <div className={styles.detailsCell}>
                    {getText('msg_dc3decbb9384')}
                </div>
            </div>
            {enrollments.map(enrollment => (
                <DesktopRow
                    key={enrollment.id}
                    enrollment={enrollment}
                    planYearId={planYearId}
                    displayEmployeeCosts={displayEmployeeCosts}
                    displayEmployerCosts={displayEmployerCosts}
                    columnCount={columnCount}
                />
            ))}
        </div>
    );
}

function MobileAccordion({
    enrollments,
    planYearId,
    displayEmployeeCosts,
    displayEmployerCosts,
}) {
    return (
        <div className={styles.accordionContainer}>
            {enrollments.map(enrollment => (
                <AccordionRow
                    key={enrollment.id}
                    enrollment={enrollment}
                    planYearId={planYearId}
                    displayEmployeeCosts={displayEmployeeCosts}
                    displayEmployerCosts={displayEmployerCosts}
                />
            ))}
        </div>
    );
}

export default function BenefitList({
    enrollments,
    planYearId,
    displayEmployeeCosts = true,
    displayEmployerCosts = true,
    emptyState,
}) {
    const isMobile = useIsMobile();

    if (!enrollments || enrollments.length === 0) {
        return (
            emptyState ?? (
                <NotFoundBox
                    backgroundColor="#f0f4ff"
                    title={getText('msg_6b97259cfa63')}
                    description={getText('msg_87ac069f5dda')}
                />
            )
        );
    }

    if (isMobile) {
        return (
            <MobileAccordion
                enrollments={enrollments}
                planYearId={planYearId}
                displayEmployeeCosts={displayEmployeeCosts}
                displayEmployerCosts={displayEmployerCosts}
            />
        );
    }

    return (
        <div className={styles.tableBleed}>
            <DesktopTable
                enrollments={enrollments}
                planYearId={planYearId}
                displayEmployeeCosts={displayEmployeeCosts}
                displayEmployerCosts={displayEmployerCosts}
            />
        </div>
    );
}
